import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Button } from '../../components/serac';

export default function SsoCallback() {
  const { t } = useTranslation(['auth', 'common']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { ssoCallback } = useAuth();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    handleCallback();
  }, []);

  async function handleCallback() {
    const token = searchParams.get('token');
    const email = searchParams.get('email') || '';
    const name = searchParams.get('name') || '';
    const error = searchParams.get('error');

    // Check for error from backend
    if (error) {
      setStatus('error');
      setErrorMessage(decodeURIComponent(error));
      return;
    }

    // Check for token
    if (!token) {
      setStatus('error');
      setErrorMessage(t('auth:sso.noToken'));
      return;
    }

    try {
      // Validate token and create session
      await ssoCallback(token, email, name);
      setStatus('success');

      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/portal/dashboard');
      }, 1500);
    } catch (err: any) {
      setStatus('error');
      setErrorMessage(err.message || t('auth:sso.ssoFailed'));
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        <Card>
          <div className="text-center py-8">
            {/* Loading State */}
            {status === 'loading' && (
              <>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-blue-600 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('auth:sso.completing')}
                </h2>
                <p className="text-gray-600">
                  {t('auth:sso.verifying')}
                </p>
              </>
            )}

            {/* Success State */}
            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('auth:sso.successTitle')}
                </h2>
                <p className="text-gray-600">
                  {t('auth:sso.successBody')}
                </p>
              </>
            )}

            {/* Error State */}
            {status === 'error' && (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('auth:sso.failedTitle')}
                </h2>
                <p className="text-gray-600 mb-4">
                  {errorMessage || t('auth:sso.unexpectedError')}
                </p>
                <div className="space-y-3">
                  <Button
                    variant="ice"
                    className="w-full"
                    onClick={() => navigate('/portal/login?tab=license-key')}
                  >
                    {t('auth:sso.tryAgain')}
                  </Button>
                  <Button
                    variant="quiet"
                    className="w-full"
                    onClick={() => navigate('/portal/login')}
                  >
                    {t('auth:sso.backToLogin')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Help text */}
        {status === 'error' && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              {t('auth:sso.helpPrefix')}{' '}
              <a
                href="mailto:support@serac.build"
                className="text-blue-600 hover:text-blue-700"
              >
                {t('auth:sso.supportLink')}
              </a>
              {t('auth:sso.helpSuffix')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
